import React from 'react';
import { toast } from 'react-hot-toast';
import { SpreadsheetData } from '../types';
import { convertSelectedCellsToClipboard } from '../utils';

interface UseSpreadsheetCopyProps {
  selectedCells: Set<string>;
  spreadsheetData: SpreadsheetData[];
  onCopy?: (copiedData: string[][]) => void;
}

export function useSpreadsheetCopy({
  selectedCells,
  spreadsheetData,
  onCopy
}: UseSpreadsheetCopyProps) {
  // Copy selected cells to clipboard
  const copySelectedCells = React.useCallback(async () => {
    if (selectedCells.size === 0) return;

    try {
      const copiedData = convertSelectedCellsToClipboard(selectedCells, spreadsheetData);

      // Convert to tab-separated format (compatible with Excel/Google Sheets)
      const clipboardText = copiedData.map(row => row.join('\t')).join('\n');

      // Copy to clipboard
      await navigator.clipboard.writeText(clipboardText);

      // Show toast notification
      toast.success('Copied to clipboard!');

      // Call callback if provided
      if (onCopy) {
        onCopy(copiedData);
      }

      console.log('Copied cells to clipboard:', copiedData);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  }, [selectedCells, spreadsheetData, onCopy]);

  // Handle copy operations within the spreadsheet container
  const handleCopyKeyDown = React.useCallback((event: React.KeyboardEvent, activeElement?: Element) => {
    // Handle copy operation
    if ((event.ctrlKey || event.metaKey) && event.key === 'c' && selectedCells.size > 0) {
      // Check if the active element is an input within our spreadsheet
      const isInputInSpreadsheet = activeElement && 
        activeElement.tagName === 'INPUT';
      
      // If it's an input in our spreadsheet and there's text selected, let browser handle it
      if (isInputInSpreadsheet) {
        const input = activeElement as HTMLInputElement;
        if (input.selectionStart !== input.selectionEnd) {
          return false; // Let browser handle text selection copy
        }
      }
      
      // Otherwise, handle spreadsheet cell copy
      event.preventDefault();
      copySelectedCells();
      return true;
    }

    return false;
  }, [selectedCells, copySelectedCells]);

  return {
    copySelectedCells,
    handleCopyKeyDown
  };
} 